import {
  Row,
  Col,
  Button,
  Input,
  Empty,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import './index.css';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getFixtures } from '../../apis/fixture.api';
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  startTransition,
} from 'react';
import {
  categories,
  HOME_PATH,
  PAGE_TITLES,
} from '../../utils/constant';
import Loader from '../../components/common/Loader';
import ConnectionBanner from '../../components/common/ConnectionBanner';
import Title from 'antd/es/typography/Title';
import { styles } from '../../styles';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import JoinDiscord from '../../components/social/discord';
import {
  fixtureListCacheKey,
  isFixtureListResponse,
  readSessionCache,
  resilientFetch,
} from '../../utils/resilientFetch';
import { CategoryCard } from './HomeFixtureCards';
import DeferredHomeDescription from './DeferredHomeDescription';
import CategoryDescription from './CategoryDescription';
import { getCategorySeoContent } from './categorySeoContent';

const searchInputStyle: React.CSSProperties = {
  borderRadius: '4px',
  width: '100%',
  margin: '12px auto',
};

function applyFixturesPayload(
  payload: any,
  setFixtures: (v: any) => void,
  setNoFixtures: (v: boolean) => void,
) {
  if (!payload?.subCategories?.[0]?.games?.length) {
    setNoFixtures(true);
  } else {
    setNoFixtures(false);
  }
  setFixtures(payload);
}

export default function Home() {
  const matches = useMediaQuery('(min-width: 1000px)');
  const { categoryName } = useParams();
  const cacheKey = fixtureListCacheKey(categoryName);
  const cachedEntry = readSessionCache<any>(cacheKey);

  const [fixtures, setFixtures] = useState<any>(() => cachedEntry?.data?.data ?? []);
  const [noFixtures, setNoFixtures] = useState<boolean>(() => {
    const payload = cachedEntry?.data?.data;
    return Boolean(payload && !payload?.subCategories?.[0]?.games?.length);
  });
  const [loader, setLoader] = useState<boolean>(() => !cachedEntry?.data?.data);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const location = useLocation();
  const isBuffstream = location.pathname.includes('/buffstream');
  const fetchIdRef = useRef(0);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getAllFixtures = useCallback(async (force = false) => {
    const requestId = ++fetchIdRef.current;
    const selectedCat = categoryName ?? 'Streams Today';
    const categoryObj = categories.find(
      (cat: any) => cat.label.toLowerCase() === selectedCat?.toLowerCase(),
    );
    const category = categoryObj?.value;
    const key = fixtureListCacheKey(categoryName);
    const existing = readSessionCache<any>(key);
    const hasCachedPayload = Boolean(existing?.data?.data);

    if (!hasCachedPayload) {
      setLoader(true);
    }

    const result = await resilientFetch<any>({
      key,
      fetcher: () => getFixtures({ categoryName: category }),
      isSuccess: isFixtureListResponse,
      skipIfFresh: !force,
      staggerMs: force ? 0 : undefined,
    });

    if (requestId !== fetchIdRef.current) return;

    if (result.data?.data) {
      applyFixturesPayload(result.data.data, setFixtures, setNoFixtures);
      setFetchError(result.error);
    } else if (!hasCachedPayload) {
      setNoFixtures(true);
      setFixtures([]);
      setFetchError(true);
    } else {
      setFetchError(true);
    }

    setLoader(false);
  }, [categoryName]);

  const filteredFixtures = useMemo(() => {
    if (!fixtures?.subCategories) return fixtures;

    let filteredSubCategories = fixtures.subCategories;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredSubCategories = filteredSubCategories
        .map((cat: any) => {
          const filteredGames = (cat.games || []).filter((game: any) => (
            game.teamA?.toLowerCase().includes(query)
            || game.teamB?.toLowerCase().includes(query)
            || cat.subCategoryName?.toLowerCase().includes(query)
          ));
          return { ...cat, games: filteredGames };
        })
        .filter((cat: any) => cat.games && cat.games.length > 0);
    }

    return { ...fixtures, subCategories: filteredSubCategories };
  }, [fixtures, searchQuery]);

  const gamesFound = useMemo(() => (
    filteredFixtures?.subCategories?.reduce(
      (acc: number, cat: any) => acc + (cat.games?.length || 0),
      0,
    ) || 0
  ), [filteredFixtures]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      startTransition(() => setSearchQuery(value));
    }, 200);
  }, []);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  useEffect(() => {
    const seo = categoryName ? getCategorySeoContent(categoryName) : null;
    document.title = categoryName
      ? (PAGE_TITLES[categoryName?.toUpperCase()]
        || PAGE_TITLES[seo?.key || '']
        || `${seo?.label || categoryName} Live Streams | SportsFeed24.pk`)
      : PAGE_TITLES.WEBSITE_TITLE;

    if (seo?.metaDescription) {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', seo.metaDescription);
    }

    const key = fixtureListCacheKey(categoryName);
    const existing = readSessionCache<any>(key);
    if (existing?.data?.data) {
      applyFixturesPayload(existing.data.data, setFixtures, setNoFixtures);
      setLoader(false);
    } else {
      setFixtures([]);
      setNoFixtures(false);
      setLoader(true);
    }
    setFetchError(false);
    setSearchInput('');
    setSearchQuery('');
    getAllFixtures();
  }, [categoryName, getAllFixtures]);

  return (
    <>
      <Row
        gutter={24}
        style={{
          justifyContent: 'center',
          display: 'flex',
          padding: '12px 12px 0',
        }}
      >
        <Col xs={24} sm={24} md={20} lg={20} xl={20} style={{ paddingBottom: '0px' }}>
          <JoinDiscord />
        </Col>
      </Row>

      <Row
        gutter={24}
        style={{
          justifyContent: 'center',
          display: 'flex',
          padding: '12px 12px',
        }}
      >
        <Col xs={24} sm={24} md={20} lg={20} xl={20}>
          <Input
            placeholder="Search fixtures, teams, or categories..."
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="large"
            style={searchInputStyle}
            allowClear
          />
          {searchQuery && (
            <div
              style={{
                textAlign: 'center',
                marginTop: 8,
                color: '#fff',
                fontSize: '14px',
              }}
            >
              {gamesFound} games found
            </div>
          )}
        </Col>
        {!categoryName && <Col xs={24} sm={24} md={6} lg={5} xl={5} />}
      </Row>

      <ConnectionBanner
        visible={fetchError && !loader}
        onRetry={() => getAllFixtures(true)}
      />

      <Row gutter={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <Col xs={24} sm={24} md={20} lg={20} xl={20}>
          {loader ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Loader />
            </div>
          ) : categoryName && noFixtures ? (
            <div>
              <Title level={3} style={{ color: '#fff' }}>
                No Fixtures Found For {categoryName.toUpperCase()}
              </Title>
              <Title level={3}>
                <Link to={HOME_PATH}>
                  <Button style={styles.homeButton}>Home</Button>
                </Link>
              </Title>
            </div>
          ) : filteredFixtures?.subCategories?.length > 0 ? (
            filteredFixtures.subCategories.map((category: any) => (
              <CategoryCard
                key={category.subCategoryName || category.subCategoryImage}
                category={category}
                categoryName={categoryName}
                isDesktop={matches}
              />
            ))
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: '#94a3b8' }}>
                  {searchQuery
                    ? 'No fixtures found matching your search'
                    : 'No fixtures available'}
                </span>
              }
              style={{
                padding: '40px 0',
                background: 'rgba(26, 26, 26, 0.5)',
                borderRadius: '8px',
              }}
            >
              {searchQuery && (
                <Button
                  type="primary"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                  }}
                >
                  Clear Search
                </Button>
              )}
            </Empty>
          )}
        </Col>
      </Row>

      <Row gutter={24} style={{ display: 'flex', justifyContent: 'center' }}>
        <Col xs={24} md={matches ? 20 : 16}>
          {!loader && !categoryName && !isBuffstream && <DeferredHomeDescription />}
          {!loader && categoryName && (
            <CategoryDescription categoryName={categoryName} />
          )}
        </Col>
      </Row>
    </>
  );
}
